import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Brand-specific animations
const logoSpin = keyframes`
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.1); }
  100% { transform: rotate(360deg) scale(1); }
`;

const brandPulse = keyframes`
  0%, 100% { 
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transform: scale(1);
  }
  50% { 
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
    transform: scale(1.1);
  }
`;

const brandDots = keyframes`
  0%, 80%, 100% { 
    transform: scale(0);
    background: #667eea;
  }
  40% { 
    transform: scale(1);
    background: #764ba2;
  }
`;

const brandWave = keyframes`
  0%, 100% { 
    transform: translateY(0px);
    background: #667eea;
  }
  50% { 
    transform: translateY(-10px);
    background: #764ba2;
  }
`;

const brandText = keyframes`
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.7; 
    transform: scale(1.05);
  }
`;

// Styled Components
const LogoLoader = styled('img')({
  width: 60,
  height: 60,
  objectFit: 'contain',
  animation: `${logoSpin} 2s ease-in-out infinite`,
});

const BrandPulseLoader = styled(Box)({
  width: 50,
  height: 50,
  borderRadius: '50%',
  animation: `${brandPulse} 1.5s ease-in-out infinite`,
});

const BrandDotsLoader = styled(Box)({
  display: 'flex',
  gap: 1,
  '& div': {
    width: 12,
    height: 12,
    borderRadius: '50%',
    animation: `${brandDots} 1.4s ease-in-out infinite both`,
    '&:nth-of-type(1)': { animationDelay: '-0.32s' },
    '&:nth-of-type(2)': { animationDelay: '-0.16s' },
    '&:nth-of-type(3)': { animationDelay: '0s' },
  },
});

const BrandWaveLoader = styled(Box)({
  display: 'flex',
  gap: 0.5,
  '& div': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    animation: `${brandWave} 1.2s ease-in-out infinite both`,
    '&:nth-of-type(1)': { animationDelay: '-0.4s' },
    '&:nth-of-type(2)': { animationDelay: '-0.3s' },
    '&:nth-of-type(3)': { animationDelay: '-0.2s' },
    '&:nth-of-type(4)': { animationDelay: '-0.1s' },
    '&:nth-of-type(5)': { animationDelay: '0s' },
  },
});

const BrandTextLoader = styled(Typography)({
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: 24,
  fontWeight: 'bold',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: `${brandText} 2s ease-in-out infinite`,
});

const BrandSpinner = styled(Box)({
  width: 40,
  height: 40,
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #667eea',
  borderRight: '4px solid #764ba2',
  borderRadius: '50%',
  animation: `${logoSpin} 1s linear infinite`,
});

// Main BrandLoader Component
export const BrandLoader = ({ 
  type = 'logo', 
  text = 'NIStylez', 
  size = 'large',
  showText = true,
  testMode = false 
}) => {
  const sizeMap = {
    small: { width: 30, height: 30, fontSize: 16 },
    medium: { width: 50, height: 50, fontSize: 20 },
    large: { width: 80, height: 80, fontSize: 24 }
  };

  const currentSize = sizeMap[size];

  const renderLoader = () => {
    switch (type) {
      case 'logo':
        return (
          <LogoLoader 
            src="/nice.jpg"
            alt="Loading..."
            sx={{ 
              width: currentSize.width, 
              height: currentSize.height 
            }} 
          />
        );
      
      case 'dots':
        return <BrandDotsLoader />;
      
      case 'wave':
        return <BrandWaveLoader />;
      
      case 'spinner':
        return (
          <BrandSpinner 
            sx={{ 
              width: currentSize.width, 
              height: currentSize.height,
              borderWidth: currentSize.width / 10
            }} 
          />
        );
      
      case 'pulse':
        return (
          <BrandPulseLoader 
            sx={{ 
              width: currentSize.width, 
              height: currentSize.height 
            }} 
          />
        );
      
      case 'text':
        return (
          <BrandTextLoader 
            sx={{ fontSize: currentSize.fontSize }}
          >
            {text}
          </BrandTextLoader>
        );
      
      default:
        return <BrandDotsLoader />;
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        alignItems: 'center', 
        gap: 2 
      }}
    >
      {renderLoader()}
      {showText && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#1976d2',
            fontSize: currentSize.fontSize * 0.7,
            fontWeight: 'bold'
          }}
        >
          Loading...
        </Typography>
      )}
    </Box>
  );
};

// Usage Examples:
/*
// Default: Large logo spinner with blue text
<BrandLoader />

// Brand color dots
<BrandLoader type="dots" />

// Wave animation
<BrandLoader type="wave" />

// Custom text
<BrandLoader type="text" text="NIStylez" />

// Pulse animation
<BrandLoader type="pulse" />

// Spinner with brand colors
<BrandLoader type="spinner" />

// Different sizes
<BrandLoader size="small" />
<BrandLoader size="medium" />
<BrandLoader size="large" />

// Without text
<BrandLoader showText={false} />
*/

export default BrandLoader;
